import type { ReactNode } from 'react';

import { cn } from '../lib/cn';

export type SegmentedOption<T extends string> = {
  value: T;
  label: ReactNode;
  /** Ligne secondaire sous le libellé (optionnelle). */
  hint?: ReactNode;
  /** Icône affichée avec le libellé (au-dessus, ou à gauche en `inline`). */
  icon?: ReactNode;
};

export type SegmentedControlProps<T extends string> = {
  /** Nom du groupe de radios, doit être unique dans le formulaire. */
  name: string;
  value: T | undefined;
  onChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  invalid?: boolean;
  /** `row` : boutons côte à côte. `grid` : cartes sur 2 colonnes. */
  layout?: 'row' | 'grid';
  /** `stacked` : icône au-dessus, texte centré. `inline` : icône à gauche, texte aligné à gauche. */
  itemLayout?: 'stacked' | 'inline';
  className?: string;
  'aria-labelledby'?: string;
};

/**
 * Choix unique sous forme de boutons segmentés. Repose sur des `<input type="radio">`
 * masqués : navigation clavier et lecteurs d'écran natifs conservés.
 */
export function SegmentedControl<T extends string>({
  name,
  value,
  onChange,
  options,
  invalid,
  layout = 'row',
  itemLayout = 'stacked',
  className,
  ...props
}: SegmentedControlProps<T>) {
  const inline = itemLayout === 'inline';

  return (
    <div
      role="radiogroup"
      className={cn(
        layout === 'row' ? 'flex flex-wrap gap-2' : 'grid grid-cols-2 gap-2',
        className,
      )}
      {...props}
    >
      {options.map((option) => (
        <label
          key={option.value}
          className={cn(
            'group relative flex flex-1 cursor-pointer rounded-field border bg-surface px-4 py-3 text-sm transition-colors',
            inline
              ? 'items-center gap-3 text-left'
              : 'flex-col items-center justify-center gap-1.5 text-center',
            'border-border-input/50 text-content hover:border-brand-600/40 hover:bg-brand-50/60',
            'has-[:checked]:border-brand-600 has-[:checked]:bg-brand-50',
            'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand-600',
            invalid && 'border-danger/60',
          )}
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            className="sr-only"
          />

          {option.icon ? (
            <span
              className={cn(
                'flex shrink-0 text-content-subtle transition-colors group-has-[:checked]:text-brand-600',
                inline && '[&>svg]:size-6',
              )}
            >
              {option.icon}
            </span>
          ) : null}

          <span className={cn('flex min-w-0 flex-col', inline ? 'items-start' : 'items-center')}>
            <span className="font-semibold leading-tight group-has-[:checked]:text-brand-600">
              {option.label}
            </span>
            {option.hint ? (
              <span className="text-xs font-normal text-content-subtle">{option.hint}</span>
            ) : null}
          </span>
        </label>
      ))}
    </div>
  );
}
